import { useEffect, useRef, useState, useCallback } from "react";
import {
  ChatCircleDots, X, PaperPlaneRight, Paperclip, Plus, CircleNotch, Sparkle,
  File as FileIcon, ClockCounterClockwise, DownloadSimple, Copy, Trash, CaretLeft, Lifebuoy,
} from "@phosphor-icons/react";
import api, { formatApiError, downloadBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { GroundingBadge, GroundingReport, VerifyGroundingButton } from "./GroundingReport";

const SID_KEY = "claro-aichat-sid";

export default function AskAiPanel() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // chat | history
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("general"); // general | help
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const createSession = useCallback(async () => {
    const r = await api.post("/ai-chat/sessions");
    localStorage.setItem(SID_KEY, r.data.id);
    setSession(r.data);
    return r.data;
  }, []);

  const ensureSession = useCallback(async () => {
    setLoading(true);
    try {
      const sid = localStorage.getItem(SID_KEY);
      if (sid) {
        try {
          const r = await api.get(`/ai-chat/sessions/${sid}`);
          setSession(r.data);
          return;
        } catch { /* stale id → make a new one */ }
      }
      await createSession();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [createSession]);

  useEffect(() => {
    if (open && !session) ensureSession();
  }, [open, session, ensureSession]);

  useEffect(() => { if (open && view === "chat") scrollToBottom(); }, [open, view, session?.messages?.length]);

  const loadHistory = async () => {
    setView("history");
    try {
      const r = await api.get("/ai-chat/sessions");
      setSessions(r.data.sessions || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openSession = async (sid) => {
    setLoading(true);
    setView("chat");
    try {
      const r = await api.get(`/ai-chat/sessions/${sid}`);
      localStorage.setItem(SID_KEY, sid);
      setSession(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/ai-chat/sessions/${sid}`);
      setSessions((s) => s.filter((x) => x.id !== sid));
      if (session?.id === sid) { localStorage.removeItem(SID_KEY); setSession(null); }
      toast.success("Chat deleted");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const newChat = async () => {
    try {
      setView("chat");
      localStorage.removeItem(SID_KEY);
      await createSession();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportPdf = async () => {
    if (!session) return;
    setExporting(true);
    try {
      await downloadBlob(`/ai-chat/sessions/${session.id}/export.pdf`, "AskAI_conversation.pdf");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Copied to clipboard");
    } catch { toast.error("Couldn't copy"); }
  };

  const onPick = (e) => {
    const picked = Array.from(e.target.files || []);
    if (picked.length) setFiles((f) => [...f, ...picked]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const removeFile = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    let s = session;
    if (!s) { s = await createSession(); }
    setSending(true);
    const optimisticUser = { id: `tmp-${Date.now()}`, role: "user", content: q, ts: new Date().toISOString(), mode };
    setSession((prev) => prev ? { ...prev, messages: [...(prev.messages || []), optimisticUser] } : prev);
    setInput("");
    scrollToBottom();
    try {
      const fd = new FormData();
      fd.append("content", q);
      fd.append("mode", mode);
      files.forEach((f) => fd.append("files", f));
      const r = await api.post(`/ai-chat/sessions/${s.id}/message`, fd);
      setFiles([]);
      setSession((prev) => {
        if (!prev) return prev;
        const msgs = (prev.messages || []).filter((m) => m.id !== optimisticUser.id);
        return { ...prev, messages: [...msgs, r.data.user_message, r.data.assistant_message], references: r.data.references };
      });
      scrollToBottom();
    } catch (e) {
      setSession((prev) => prev ? { ...prev, messages: (prev.messages || []).filter((m) => m.id !== optimisticUser.id) } : prev);
      setInput(q);
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  const verify = async (mid) => {
    if (!session) return;
    setVerifying(mid);
    try {
      const r = await api.post(`/ai-chat/sessions/${session.id}/messages/${mid}/verify`);
      setSession((prev) => prev ? {
        ...prev,
        messages: (prev.messages || []).map((m) => m.id === mid ? { ...m, grounding_check: r.data } : m),
      } : prev);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(null);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  if (!user) return null;

  return (
    <>
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="ask-ai-launcher"
          className="fixed bottom-5 left-5 z-40 flex items-center gap-1.5 bg-primary text-white shadow-lg hover:bg-primary/90 px-3 py-2 text-sm rounded-none">
          <ChatCircleDots size={18} weight="fill" />
          Ask AI
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="ask-ai-panel">
          <div className="flex-1 bg-black/20" onClick={() => setOpen(false)} />
          <div className="w-full max-w-md h-full bg-background border-l border-border shadow-2xl flex flex-col">
            {/* header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-[#071B3E] text-white">
              <div className="flex items-center gap-2">
                <Sparkle size={18} weight="fill" />
                <div>
                  <div className="text-sm font-semibold leading-tight">Ask AI</div>
                  <div className="text-[11px] opacity-70 leading-tight">Claude Sonnet 4.5 · 1 credit / question</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={loadHistory} title="Chat history" data-testid="ask-ai-history-btn"
                  className="p-1.5 hover:bg-white/10 rounded-none"><ClockCounterClockwise size={16} /></button>
                <button type="button" onClick={exportPdf} disabled={exporting || !(session?.messages || []).length}
                  title="Download this conversation as PDF" data-testid="ask-ai-export"
                  className="p-1.5 hover:bg-white/10 rounded-none disabled:opacity-40">
                  {exporting ? <CircleNotch size={16} className="animate-spin" /> : <DownloadSimple size={16} />}
                </button>
                <button type="button" onClick={newChat} title="New chat" data-testid="ask-ai-new"
                  className="p-1.5 hover:bg-white/10 rounded-none"><Plus size={16} /></button>
                <button type="button" onClick={() => setOpen(false)} title="Close" data-testid="ask-ai-close"
                  className="p-1.5 hover:bg-white/10 rounded-none"><X size={16} /></button>
              </div>
            </div>

            {view === "history" ? (
              <div className="flex-1 overflow-y-auto" data-testid="ask-ai-history">
                <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
                  <button type="button" onClick={() => setView("chat")} data-testid="ask-ai-history-back"
                    className="text-xs inline-flex items-center gap-1 hover:underline"><CaretLeft size={12} /> Back to chat</button>
                </div>
                {!sessions.length && (
                  <div className="text-sm text-muted-foreground text-center mt-10" data-testid="ask-ai-history-empty">No past conversations yet.</div>
                )}
                {sessions.map((s) => (
                  <div key={s.id} onClick={() => openSession(s.id)} data-testid={`ask-ai-history-item-${s.id}`}
                    className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border hover:bg-card cursor-pointer">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{s.title || "New chat"}</div>
                      <div className="text-[11px] text-muted-foreground">{(s.updated_at || "").slice(0, 16).replace("T", " ")}</div>
                    </div>
                    <button type="button" onClick={(e) => deleteSession(s.id, e)} title="Delete"
                      data-testid={`ask-ai-history-delete-${s.id}`}
                      className="p-1.5 text-muted-foreground hover:text-red-600 shrink-0"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            ) : (
              <>
                {/* messages */}
                <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="ask-ai-messages">
                  {loading && <div className="text-xs text-muted-foreground flex items-center gap-2"><CircleNotch size={14} className="animate-spin" /> Loading…</div>}
                  {!loading && !(session?.messages || []).length && (
                    <div className="text-sm text-muted-foreground text-center mt-10" data-testid="ask-ai-empty">
                      <ChatCircleDots size={32} className="mx-auto mb-3 opacity-40" />
                      Ask any question. Attach reference documents to ground the answer, or switch to
                      <span className="font-medium"> Ask Claro</span> for help using the platform.
                    </div>
                  )}
                  {(session?.messages || []).map((m) => (
                    <div key={m.id} data-testid={`ask-ai-msg-${m.role}`}
                      className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                      <div className={`max-w-[85%] ${m.role === "user" ? "bg-primary text-white" : "bg-card border border-border"} px-3 py-2 text-sm whitespace-pre-wrap`}>
                        {m.mode === "help" && (
                          <div className="text-[10px] uppercase tracking-wide opacity-70 mb-1 inline-flex items-center gap-1"><Lifebuoy size={11} /> Ask Claro</div>
                        )}
                        {(m.refs || []).length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-1.5">
                            {m.refs.map((n, i) => (
                              <span key={i} className="text-[10px] bg-white/15 px-1.5 py-0.5 inline-flex items-center gap-1"><FileIcon size={10} />{n}</span>
                            ))}
                          </div>
                        )}
                        <div>{m.content}</div>
                        {m.role === "assistant" && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            <GroundingBadge check={m.grounding_check} testid={`ask-ai-badge-${m.id}`} />
                            <VerifyGroundingButton
                              onClick={() => verify(m.id)}
                              busy={verifying === m.id}
                              checked={!!m.grounding_check}
                              testid={`ask-ai-verify-${m.id}`}
                              title="Check this answer for hallucination / grounding (1 AI credit)" />
                            <button type="button" onClick={() => copyText(m.content)} data-testid={`ask-ai-copy-${m.id}`}
                              className="text-[11px] inline-flex items-center gap-1 border border-border px-2 py-0.5 hover:bg-card">
                              <Copy size={12} /> Copy
                            </button>
                          </div>
                        )}
                        {m.role === "assistant" && m.grounding_check && (
                          <div className="mt-2"><GroundingReport check={m.grounding_check} testid={`ask-ai-report-${m.id}`} /></div>
                        )}
                      </div>
                    </div>
                  ))}
                  {sending && (
                    <div className="flex justify-start" data-testid="ask-ai-thinking">
                      <div className="bg-card border border-border px-3 py-2 text-sm text-muted-foreground flex items-center gap-2">
                        <CircleNotch size={14} className="animate-spin" /> Thinking…
                      </div>
                    </div>
                  )}
                </div>

                {/* mode toggle */}
                <div className="px-4 pt-2 flex items-center gap-1 border-t border-border">
                  <button type="button" onClick={() => setMode("general")} data-testid="ask-ai-mode-general"
                    className={`text-[11px] px-2 py-1 border ${mode === "general" ? "bg-primary text-white border-primary" : "border-border hover:bg-card"}`}>
                    General
                  </button>
                  <button type="button" onClick={() => setMode("help")} data-testid="ask-ai-mode-help"
                    className={`text-[11px] px-2 py-1 border inline-flex items-center gap-1 ${mode === "help" ? "bg-primary text-white border-primary" : "border-border hover:bg-card"}`}>
                    <Lifebuoy size={12} /> Ask Claro (help)
                  </button>
                </div>

                {/* attachments preview */}
                {files.length > 0 && (
                  <div className="px-4 py-2 flex flex-wrap gap-1.5" data-testid="ask-ai-attachments">
                    {files.map((f, i) => (
                      <span key={i} className="text-[11px] bg-muted px-2 py-1 inline-flex items-center gap-1.5">
                        <FileIcon size={12} />{f.name}
                        <button type="button" onClick={() => removeFile(i)} className="hover:text-red-600"><X size={11} /></button>
                      </span>
                    ))}
                  </div>
                )}

                {/* composer */}
                <div className="px-4 py-3 border-t border-border">
                  <div className="flex items-end gap-2">
                    <input ref={fileRef} type="file" multiple hidden onChange={onPick}
                      accept=".pdf,.docx,.xlsx,.txt,.md,.csv,.json,.log" data-testid="ask-ai-file-input" />
                    <button type="button" onClick={() => fileRef.current?.click()} title="Attach reference documents"
                      disabled={mode === "help"} data-testid="ask-ai-attach"
                      className="p-2 border border-border hover:bg-card rounded-none shrink-0 disabled:opacity-40">
                      <Paperclip size={16} />
                    </button>
                    <textarea
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={onKey}
                      rows={1}
                      placeholder={mode === "help" ? "Ask how to use ClaroShield…" : "Ask a question…"}
                      data-testid="ask-ai-input"
                      className="flex-1 resize-none border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary max-h-32" />
                    <button type="button" onClick={send} disabled={sending || !input.trim()}
                      data-testid="ask-ai-send"
                      className="p-2 bg-primary text-white hover:bg-primary/90 disabled:opacity-40 rounded-none shrink-0">
                      {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneRight size={16} weight="fill" />}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
